// Landing "Open App" hands off with `app/?code=<one-time SSO code>` (SEC-34).
// The code must leave the address bar before anything records the URL:
// posthog-js captures `$current_url` (and session replay records URLs) as soon
// as it initializes, which happens at module import time on web. So the web
// PostHog provider calls `captureSsoHandoffCode()` before `init`, and the root
// layout later picks the code up with `takeSsoHandoffCode()` to exchange it.
// Pure module (no RN imports) so it is unit-testable.

export const SSO_HANDOFF_PARAM = 'code';

type HandoffWindow = {
  location: { href: string };
  history: { replaceState: (data: unknown, unused: string, url?: string) => void };
  document?: { title?: string };
};

let pendingCode: string | null = null;
let captured = false;

function defaultWindow(): HandoffWindow | null {
  if (typeof window === 'undefined' || !window.location || !window.history) return null;
  return window as unknown as HandoffWindow;
}

/** Remove the SSO handoff code from a URL string; returns the input unchanged if it has none or isn't a URL. */
export function stripSsoHandoffParam(rawUrl: string): string {
  if (!rawUrl || !rawUrl.includes(`${SSO_HANDOFF_PARAM}=`)) return rawUrl;
  try {
    const url = new URL(rawUrl);
    if (!url.searchParams.has(SSO_HANDOFF_PARAM)) return rawUrl;
    url.searchParams.delete(SSO_HANDOFF_PARAM);
    return url.toString();
  } catch {
    return rawUrl;
  }
}

/**
 * Read the SSO code from the current URL, strip it from the address bar with
 * `history.replaceState`, and keep it in memory. Idempotent: only the first
 * call per page load looks at the URL.
 */
export function captureSsoHandoffCode(win: HandoffWindow | null = defaultWindow()): string | null {
  if (captured || !win) return pendingCode;
  captured = true;
  try {
    const url = new URL(win.location.href);
    const code = url.searchParams.get(SSO_HANDOFF_PARAM);
    if (!code) return pendingCode;
    pendingCode = code;
    url.searchParams.delete(SSO_HANDOFF_PARAM);
    win.history.replaceState({}, win.document?.title ?? '', url.toString());
  } catch {
    // Malformed URL or a locked-down history API: nothing to hand off.
  }
  return pendingCode;
}

/** Return the captured SSO code once (then forget it). */
export function takeSsoHandoffCode(win: HandoffWindow | null = defaultWindow()): string | null {
  const code = captureSsoHandoffCode(win);
  pendingCode = null;
  return code;
}

// PostHog event properties that carry a full URL.
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];

// Query params that must never reach analytics: the SSO handoff code, and the
// email the landing page / expired-link screen pass to /login?email=…
const SENSITIVE_URL_PARAMS = [SSO_HANDOFF_PARAM, 'email'];

/** Drop the SSO code and any email from a URL before it is sent to analytics. */
export function stripSensitiveUrlParams(rawUrl: string): string {
  if (!rawUrl || !SENSITIVE_URL_PARAMS.some((param) => rawUrl.includes(`${param}=`))) return rawUrl;
  try {
    const url = new URL(rawUrl);
    if (!SENSITIVE_URL_PARAMS.some((param) => url.searchParams.has(param))) return rawUrl;
    SENSITIVE_URL_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url.toString();
  } catch {
    return rawUrl;
  }
}

type PostHogLikeEvent = {
  properties?: Record<string, unknown> | null;
  $set?: Record<string, unknown> | null;
  $set_once?: Record<string, unknown> | null;
};

function scrubBag(bag: Record<string, unknown> | null | undefined) {
  if (!bag) return;
  for (const key of URL_PROPERTIES) {
    const value = bag[key];
    if (typeof value === 'string') bag[key] = stripSensitiveUrlParams(value);
  }
}

/** posthog-js `before_send` hook: drop the SSO code and any email from every URL property. */
export function scrubSsoHandoffFromEvent<T extends PostHogLikeEvent | null>(event: T): T {
  if (!event) return event;
  scrubBag(event.properties);
  scrubBag(event.$set);
  scrubBag(event.$set_once);
  scrubBag((event.properties?.$set as Record<string, unknown> | undefined) ?? null);
  scrubBag((event.properties?.$set_once as Record<string, unknown> | undefined) ?? null);
  return event;
}

/** Test-only: reset module state between cases. */
export function __resetSsoHandoffForTests() {
  pendingCode = null;
  captured = false;
}
